import * as React from 'react';
import { type ReactNode, Ref } from 'react';

import {
	CTA,
	Richtext,
	Text,
	TextTag,
	ThemeProvider,
	TokenTextAppearance,
	TokenTextColor,
	styled
} from '@volkswagen-onehub/components-core';
import { Close } from '@volkswagen-onehub/icons-core';
import { css } from 'styled-components';

import { testId } from '../../test-utils/constants';

interface TextWithReferences {
	[k: string]: string;
}

interface DisclaimerCloseButtonProps {
	closeAriaLabel: string;
	closeDisclaimer: () => void;
	closeDisclaimerRef: Ref<HTMLButtonElement>;
}

export function DisclaimerCloseButton({
	closeAriaLabel,
	closeDisclaimer,
	closeDisclaimerRef
}: DisclaimerCloseButtonProps) {
	return (
		<StyledCloseButtonWrapper>
			<ThemeProvider theme="inverted" variant="v26">
				<CTA
					tag="button"
					aria-label={closeAriaLabel}
					emphasis="tertiary"
					data-testid={testId('Disclaimer_Close_Button')}
					size="small"
					icon={<Close aria-hidden />}
					onClick={closeDisclaimer}
					ref={closeDisclaimerRef}
				/>
			</ThemeProvider>
		</StyledCloseButtonWrapper>
	);
}

// This is the part that's used by non-victor to render content
interface NoVictorContentProps {
	reference: string;
	content: string;
}

function NoVictorContent({ reference, content }: NoVictorContentProps) {
	return (
		<StyledDl key={reference}>
			<StyledDt>
				<Text
					tag={TextTag.p}
					appearance={TokenTextAppearance.copy100}
					color={TokenTextColor.inverted}
					staticSize={false}
				>
					{reference}
				</Text>
			</StyledDt>
			<StyledDd>
				<Text
					tag={TextTag.p}
					appearance={TokenTextAppearance.copy100}
					color={TokenTextColor.inverted}
					staticSize={false}
				>
					<Richtext markdown={content} dangerouslyParseRawHTML />
				</Text>
			</StyledDd>
		</StyledDl>
	);
}

interface AdditionalContentProps {
	text: string | TextWithReferences;
}

export function AdditionalContent({ text }: AdditionalContentProps) {
	if (typeof text === 'object') {
		return Object.entries(text).map(([key, value]) => <NoVictorContent key={key} reference={key} content={value} />);
	}

	return <NoVictorContent reference="i" content={text} />;
}

// This is the part that's used by victor data to render content
export function VictorContent({ children }: { children: ReactNode }) {
	return (
		<StyledDl>
			<StyledDt />
			<StyledDd>
				<Text
					tag={TextTag.p}
					appearance={TokenTextAppearance.copy100}
					color={TokenTextColor.inverted}
					staticSize={false}
				>
					{children}
				</Text>
			</StyledDd>
		</StyledDl>
	);
}

const StyledCloseButtonWrapper = styled.div`
	position: absolute;
	text-align: right;

	${({ theme }) => {
		const position = theme.direction === 'ltr' ? 'right' : 'left';

		return css`
			top: ${theme.size.static150};
			${position}: ${theme.size.static150};
		`;
	}}
`;

const StyledDl = styled.dl`
	display: grid;
	margin: 0;
	padding: 0;

	${({ theme }) => css`
		grid-template-columns: ${theme.size.dynamic0100} auto;
		grid-column-gap: ${theme.size.static150};
		grid-row-gap: ${theme.size.static200};
	`}
`;

const StyledDd = styled.dd`
	margin: 0;
	padding: 0;
`;

const StyledDt = styled.dt`
	margin: 0;
	padding: 0;
`;
